import { useState } from 'react';

import {
  APT_TYPE_LABEL,
  ErrorBox,
  INSTALL_COMPLETE_LABEL,
  Spinner,
  formatDate,
  formatPhone,
  useAsync,
} from '@solar/ui';

import { api } from '../api';

const SIZE = 30;

/**
 * 철거 관리 목록 — 레거시 WebService/uninstall/list.blade.php 대응.
 * 철거 신청(uninstall_status='Y')된 주문만 서버가 걸러서 준다(id 역순).
 * 검색어 대상(이름/연락처/주소/건물명/동/호)도 서버가 레거시 그대로 따른다.
 */
export function UninstallTable({
  selected,
  onSelect,
  version,
}: {
  selected: number | null;
  onSelect: (id: number) => void;
  version: number;
}) {
  const [keyword, setKeyword] = useState('');
  const [applied, setApplied] = useState('');
  const [page, setPage] = useState(1);

  const orders = useAsync(
    () => api.listUninstallOrders({ keyword: applied, page, size: SIZE }),
    [applied, page, version],
  );

  const total = orders.data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / SIZE));

  return (
    <>
      <div className="toolbar">
        <form
          className="search"
          onSubmit={(e) => {
            e.preventDefault();
            setApplied(keyword.trim());
            setPage(1);
          }}
        >
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="이름 · 연락처 · 주소"
          />
          <button type="submit">검색</button>
        </form>
      </div>

      {orders.loading && <Spinner />}
      {orders.error && <ErrorBox message={orders.error} onRetry={orders.reload} />}

      {orders.data && (
        <table className="grid">
          <thead>
            <tr>
              <th>시민이름</th>
              <th>연락처</th>
              <th>주소</th>
              <th>유형</th>
              <th>상품명</th>
              <th>설치 상태</th>
              <th>설치 기사</th>
              <th>설치일</th>
            </tr>
          </thead>
          <tbody>
            {orders.data.items.length === 0 && (
              <tr>
                <td colSpan={8} className="muted">
                  철거 신청된 주문이 없습니다.
                </td>
              </tr>
            )}
            {orders.data.items.map((order) => (
              <tr
                key={order.id}
                className={order.id === selected ? 'selected' : ''}
                onClick={() => onSelect(order.id)}
              >
                <td>{order.user_name ?? '-'}</td>
                <td>{formatPhone(order.user_phone)}</td>
                <td className="addr">
                  {[order.addr_full, order.building_name, order.apart_number]
                    .filter(Boolean)
                    .join(' ') || '-'}
                </td>
                <td>{APT_TYPE_LABEL[order.apt_type ?? 0] ?? '-'}</td>
                <td>{order.product_name ?? '-'}</td>
                <td>{INSTALL_COMPLETE_LABEL[order.install_complete ?? ''] ?? '-'}</td>
                <td>{order.engineer_name ?? '-'}</td>
                <td>{formatDate(order.install_date)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="pager">
        <button type="button" onClick={() => setPage((p) => p - 1)} disabled={page <= 1}>
          이전
        </button>
        <span>
          {page} / {lastPage} (총 {total}건)
        </span>
        <button type="button" onClick={() => setPage((p) => p + 1)} disabled={page >= lastPage}>
          다음
        </button>
      </div>
    </>
  );
}
